import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Info, RotateCcw, CheckCircle2, ArrowRight, ShieldAlert } from "lucide-react";
import { DUE_DILIGENCE_ITEMS } from "../data/content";
import { SectionHeader, CTABand } from "./Home";

const STORAGE_KEY = "tt_dd_tracker";

const TrackerItem = ({ item, checked, onToggle }) => {
  const [tipOpen, setTipOpen] = useState(false);
  return (
    <li data-testid={`tracker-item-${item.id}`} className={`border p-6 transition-colors ${checked ? "border-[#1D504C]/40 bg-[#1D504C]/5" : "border-[#113835]/12 bg-white"}`}>
      <div className="flex items-start gap-4">
        <button
          type="button"
          role="checkbox"
          aria-checked={checked}
          data-testid={`tracker-check-${item.id}`}
          onClick={onToggle}
          className={`mt-0.5 w-6 h-6 shrink-0 border flex items-center justify-center transition-colors ${checked ? "bg-[#113835] border-[#113835] text-[#F4D387]" : "border-[#113835]/30 hover:border-[#C1A74A]"}`}
          aria-label={`Mark ${item.title} as ${checked ? "incomplete" : "complete"}`}
        >
          {checked && <CheckCircle2 size={15} strokeWidth={1.75} />}
        </button>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <p className={`font-heading text-sm font-semibold ${checked ? "text-[#1D504C] line-through decoration-[#C1A74A]/60" : "text-[#113835]"}`}>{item.title}</p>
            {item.mandatory && (
              <span data-testid={`tracker-mandatory-${item.id}`} className="tt-status-badge bg-[#F4D387]/50 text-[#8a6d1f] flex items-center gap-1">
                <ShieldAlert size={11} strokeWidth={1.75} /> Deal-breaker
              </span>
            )}
          </div>
          {tipOpen && (
            <p data-testid={`tracker-tip-${item.id}`} className="mt-3 text-sm text-gray-600 font-light leading-relaxed border-l-2 border-[#C1A74A] pl-4">
              {item.tip}
            </p>
          )}
        </div>
        <button
          type="button"
          data-testid={`tracker-tip-toggle-${item.id}`}
          onClick={() => setTipOpen(!tipOpen)}
          className={`shrink-0 flex items-center gap-1.5 text-[0.68rem] uppercase tracking-[0.14em] font-heading transition-colors ${tipOpen ? "text-[#113835]" : "text-[#C1A74A] hover:text-[#113835]"}`}
          aria-expanded={tipOpen}
        >
          <Info size={14} strokeWidth={1.5} /> {tipOpen ? "Hide tip" : "View tips"}
        </button>
      </div>
    </li>
  );
};

export default function DueDiligenceTrackerPage() {
  const [checked, setChecked] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(checked));
  }, [checked]);

  const doneCount = DUE_DILIGENCE_ITEMS.filter((i) => checked[i.id]).length;
  const mandatoryDone = DUE_DILIGENCE_ITEMS.filter((i) => i.mandatory && checked[i.id]).length;
  const pct = Math.round((doneCount / DUE_DILIGENCE_ITEMS.length) * 100);

  return (
    <div data-testid="tracker-page">
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <SectionHeader
            eyebrow="Free Tool · The Terraa Checklist"
            title="Interactive Due Diligence Tracker"
            sub="Doing your own groundwork? Work through the ten checks every safe property purchase needs. Your progress saves automatically on this device — come back anytime."
            className="mb-12"
          />

          <div className="border border-[#113835]/12 p-6 mb-10" data-testid="tracker-progress">
            <div className="flex items-center justify-between mb-3">
              <p className="font-heading text-xs uppercase tracking-[0.18em] text-[#1D504C]">Your progress</p>
              <p className="font-heading text-sm font-semibold text-[#113835]" data-testid="tracker-progress-count">{doneCount}/{DUE_DILIGENCE_ITEMS.length} complete · {mandatoryDone}/2 deal-breakers</p>
            </div>
            <div className="h-2 bg-[#113835]/10">
              <div className="h-2 bg-[#C1A74A] transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
            {doneCount > 0 && (
              <button
                data-testid="tracker-reset"
                onClick={() => setChecked({})}
                className="mt-4 flex items-center gap-1.5 text-[0.68rem] uppercase tracking-[0.14em] font-heading text-gray-400 hover:text-red-600 transition-colors"
              >
                <RotateCcw size={12} strokeWidth={1.5} /> Reset checklist
              </button>
            )}
          </div>

          <ul className="space-y-4" data-testid="tracker-list">
            {DUE_DILIGENCE_ITEMS.map((item) => (
              <TrackerItem
                key={item.id}
                item={item}
                checked={!!checked[item.id]}
                onToggle={() => setChecked({ ...checked, [item.id]: !checked[item.id] })}
              />
            ))}
          </ul>

          <div className="mt-14 bg-[#113835] tt-grain p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <p className="font-heading text-base font-medium text-white">Stuck on any item — or want it all verified at the source?</p>
              <p className="text-sm text-white/65 font-light mt-2">Our examiners run every one of these checks and score your property out of 1000.</p>
            </div>
            <Link to="/request-title-search" data-testid="tracker-cta" className="tt-btn-gold shrink-0">
              Request Property Verification <ArrowRight size={15} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
      <CTABand />
    </div>
  );
}
